import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

// Feature flag for premium gating - set to false for early growth phase
const SUPER_POP_REQUIRES_PREMIUM = false;

export const useSuperPop = () => {
  const { user, profile } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [superPoppedIds, setSuperPoppedIds] = useState<Set<string>>(new Set());

  // Keep isPremium for future re-monetization
  const isPremium = profile?.is_premium ?? false;
  
  // During growth phase, all users can Super Pop
  const canUseSuperPop = SUPER_POP_REQUIRES_PREMIUM ? isPremium : true;

  const superPop = useCallback(async (targetId: string) => {
    if (!user) return { success: false, error: "Not authenticated" };
    if (SUPER_POP_REQUIRES_PREMIUM && !isPremium) {
      return { success: false, error: "Feature requires subscription" };
    }
    if (superPoppedIds.has(targetId)) return { success: false, error: "Already Super Popped" };

    setLoading(true);
    try {
      const { error } = await supabase
        .from("super_pops")
        .insert({ popper_id: user.id, popped_id: targetId });

      if (error) {
        // Check for unique constraint violation
        if (error.code === "23505") {
          return { success: false, error: "Already Super Popped this person" };
        }
        return { success: false, error: error.message };
      }

      setSuperPoppedIds((prev) => new Set(prev).add(targetId));
      return { success: true };
    } catch (err) {
      return { success: false, error: (err as Error).message };
    } finally {
      setLoading(false);
    }
  }, [user, isPremium, superPoppedIds]);

  const hasSuperPopped = useCallback((targetId: string) => {
    return superPoppedIds.has(targetId);
  }, [superPoppedIds]);

  // Fetch existing super pops on mount
  const fetchSuperPops = useCallback(async () => {
    if (!user) return;

    const { data } = await supabase
      .from("super_pops")
      .select("popped_id")
      .eq("popper_id", user.id);

    if (data) {
      setSuperPoppedIds(new Set(data.map((sp) => sp.popped_id)));
    }
  }, [user]);

  return {
    superPop,
    hasSuperPopped,
    loading,
    isPremium,
    canUseSuperPop,
    fetchSuperPops,
  };
};
